import { provideHttpClient, withFetch } from '@angular/common/http';
import '@angular/common/locales/global/ar-AE';
import '@angular/common/locales/global/de';
import '@angular/common/locales/global/en-AU';
import '@angular/common/locales/global/en-CA';
import '@angular/common/locales/global/en-GB';
import '@angular/common/locales/global/en-NZ';
import '@angular/common/locales/global/en-ZA';
import '@angular/common/locales/global/fr';
import '@angular/common/locales/global/nl';
import {
    ApplicationConfig,
    EnvironmentProviders,
    LOCALE_ID,
    Provider,
    importProvidersFrom,
    inject,
    provideAppInitializer,
    provideExperimentalZonelessChangeDetection
} from '@angular/core';
import { provideClientHydration, withEventReplay, withNoHttpTransferCache } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideDfApi } from '@df/api/df-api.providers';
import { provideDfBrowser } from '@df/browser/df-browser.providers';
import { provideDfCatalogUi } from '@df/catalog-ui/df-catalog-ui.providers';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import '@df/catalog-ui/listing/sort/listing-sort-favourites.engine';
import { provideDfCatalogUiRecentlyViewed } from '@df/catalog-ui/recently-viewed/recently-viewed.providers';
import '@df/catalog-ui/related-products/category-engine';
import '@df/catalog-ui/related-products/manual-engine';
import '@df/catalog-ui/related-products/related-engine';
import '@df/catalog-ui/related-products/upsell-engine';
import { AttributeResource } from '@df/catalog/attribute/attribute.resource';
import { CategoryResource } from '@df/catalog/category/category.resource';
import { CustomformResource } from '@df/catalog/customform/customform.resource';
import { provideDfCatalog } from '@df/catalog/df-catalog.providers';
import { Store } from '@df/catalog/store/store';
import { provideDfCheckout } from '@df/checkout/df-checkout.providers';
import { App } from '@df/core/app';
import { provideDfCore } from '@df/core/df-core.providers';
import { MultisiteService } from '@df/core/service/multisite';
import { provideDfDom } from '@df/dom/df-dom.providers';
import { provideDfModuleAb } from '@df/module-ab/ab.providers';
import { provideDfModuleAbCms2 } from '@df/module-ab/cms2/ab-cms2.providers';
import { AdvancedCommerceConfigStore, AdvancedCommerceSearchService, AdvancedCommerceService } from '@df/module-advanced-commerce';
import { provideDfModuleAdvancedCommerce } from '@df/module-advanced-commerce/advanced-commerce.providers';
import { provideDfModuleElasticsearch } from '@df/module-elasticsearch/elasticsearch.providers';
import { provideDfModuleGiftcards } from '@df/module-giftcards/giftcards.providers';
import { provideDfModuleGoogleMaps } from '@df/module-google-maps/google-maps.providers';
import { DfModuleGtmModule } from '@df/module-gtm/df-module-gtm.module';
import { GtmService } from '@df/module-gtm/gtm.service';
import { provideDfModuleHeaderUi } from '@df/module-header-ui/df-header-ui.providers';
import { provideDfModuleImage } from '@df/module-image/image.providers';
import '@df/module-klevu-search/klevu-context-recommendations-basket-engine';
import '@df/module-klevu-search/klevu-context-recommendations-engine';
import '@df/module-klevu-search/klevu-recommendations-engine';
import { provideDfModuleKlevuSearch } from '@df/module-klevu-search/klevu-search.providers';
import { KlevuSearchService } from '@df/module-klevu-search/klevu-search.service';
import { KlevuTrackService } from '@df/module-klevu-search/klevu-track.service';
import '@df/module-klevu-search/listing-sort-klevu.engine';
import { provideDfModuleLoqate } from '@df/module-loqate/df-module-loqate.providers';
import { MentionMeService } from '@df/module-mention-me/mention-me.service';
import { provideDfModuleOneTrust } from '@df/module-onetrust/onetrust.providers';
import { DfModulePaypalModule } from '@df/module-paypal/df-module-paypal.module';
import { provideDfModulePca } from '@df/module-pca/pca.providers';
import { provideDfModuleSentry } from '@df/module-sentry/sentry.providers';
import { DfModuleStripeModule } from '@df/module-stripe/df-module-stripe.module';
import { StripeService } from '@df/module-stripe/stripe.service';
import { provideDfModuleThumbor } from '@df/module-thumbor/df-thumbor.providers';
import { provideDfSearch } from '@df/search/df-search.providers';
import { SearchResult } from '@df/search/search-result';
import { SearchResultFactoryService } from '@df/search/search-result-factory.service';
import { SearchService } from '@df/search/search.service';
import { Basket } from '@df/session/api/basket/basket';
import '@df/session/api/basket/basket-item';
import { HistoryOrder } from '@df/session/api/history/history-order';
import { CustomerLoggedGuard } from '@df/session/customer/customer-logged.guard';
import { CustomerNotLoggedGuard } from '@df/session/customer/customer-not-logged.guard';
import { provideDfSession } from '@df/session/df-session.providers';
import { NeedLoginService } from '@df/session/need-login.service';
import { NUi } from '@df/ui/df-ui.config';
import { provideDfUi } from '@df/ui/df-ui.providers';
import { ModalModule } from '@df/ui/modal/modal.module';
import { provideDfSsr } from '@df/universal/client/df-ssr.providers';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsPageResource } from '@icms/core/resource/page/icms-page.resource';
import { provideAccount } from './account/account';
import { AccountInformationCentreModule } from './account/information-centre/account-information-centre.module';
import { LocalHistoryOrder } from './account/order/history/history-order.decorator';
import { appRoutes } from './app.routes';
import { BasketCarryOverModule } from './basket/carry-over/basket-carry-over.module';
import { LocalBasket } from './basket/local-basket';
import './catalog/config/page.config';
import './catalog/model/category/category.decorator';
import './catalog/model/store/store.decorator';
import { ProductListingFactoryDecorator } from './catalog/ui/listing/product-listing.factory.decorator';
import { LocalPageResource } from './catalog/ui/page/component/page.decorator';
import './catalog/ui/related-products/color-find-engine';
import { provideCmsComponents } from './cms/cms.module';
import './cms/config/account.cms-config';
import './cms/config/attribute-order.cms-config';
import './cms/config/checkout.cms-config';
import './cms/config/newsletter.cms-config';
import './cms/config/sizebay.cms-config';
import './cms/config/zendesk.cms-config';
import './customer/basket/basket-item.decorator';
import { CustomerGroupsModule } from './customer/groups/customer-groups.module';
import { LocalCustomerLoggedGuard } from './customer/guards/local-customer-logged.guard';
import { LocalCustomerNotLoggedGuard } from './customer/guards/local-customer-not-logged.guard';
import { LocalRespectMustLoginService } from './customer/util/local-respect-must-login.service';
import { NeedLoginServiceDecorator } from './customer/util/need-login.service.decorator';
import { EpsilonModule } from './epsilon/epsilon.module';
import { ICMS_CONFIG } from './icms.config';
import { headerComponentId } from './layout/page/header/page-header.component';
import { LocalAdvancedCommerceService } from './module/advanced-commerce/advanced-commerce.service.decorator';
import { LocalGtmService } from './module/gtm/gtm.service.decorator';
import { HubspotModule } from './module/hubspot/hubspot.module';
import { LocalKlevuSearchService } from './module/klevu-search/klevu-search.service.decorator';
import { LocalKlevuTrackService } from './module/klevu-search/klevu-track.service.decorator';
import { LocalMentionMeService } from './module/mention-me/local-mention-me.service';
import { VipModule } from './module/vip/vip.module';
import { LocalSearchResultFactoryService } from './search/search-result-factory.decorator.service';
import { LocalSearchResult } from './search/search-result.decorator';
import './ui/form/validate-pattern.config';
import { LocalNexusApiService } from './api/local-nexus-api.service';
import { NexusApiService } from '@df/api/nexus/nexus-api.service';

const providers: (Provider | EnvironmentProviders)[] = [];
if (typeof window !== 'undefined') {
    providers.push(provideExperimentalZonelessChangeDetection());
}

export const appConfig: ApplicationConfig = {
    providers: [
        ...providers,
        { provide: NexusApiService, useClass: LocalNexusApiService },
        provideDfApi(),
        provideDfBrowser(),
        provideClientHydration(withEventReplay(), withNoHttpTransferCache()),
        provideDfCatalogUiRecentlyViewed(),
        provideDfCore(),
        provideDfModuleAb({
            convertcom: {
                script: (app: App) => {
                    if (app.dfConfig.environment === 'production') {
                        return `https://cdn-4.convertexperiments.com/js/10042857-10044136.js`;
                    }
                    return '';
                }
            }
        }),
        provideDfModuleAbCms2({
            componentHideOption: false,
            binding: false
        }),

        provideDfUi({
            price: {
                precision: 2
            }
        }),
        provideHttpClient(withFetch()),
        provideAnimations(),
        provideAccount(),
        provideDfCatalogUi({
            listing: {
                category: {
                    sort: [
                        {
                            label: 'Recommended',
                            default: true,
                            engine: 'klevu'
                        },
                        {
                            label: 'Price low - high',
                            code: 'price'
                        },
                        {
                            label: 'Price high - low',
                            code: 'price',
                            desc: true
                        }
                    ],
                    size: [
                        {
                            size: 1,
                            onSize: ['S']
                        },
                        {
                            size: 2,
                            onSize: ['S', 'M']
                        },
                        {
                            size: 3,
                            onSize: ['M', 'L', 'X']
                        },
                        {
                            size: 4,
                            onSize: ['L', 'X']
                        }
                    ]
                }
            }
        }),
        provideDfCatalog(),
        provideDfCheckout({
            autoPickShippingMethod: true,
            defaults: {
                billing: {
                    to_shipping: false
                },
                newsletter: undefined
            },
            useCheckoutEndpoint: true
        }),
        provideDfDom(),
        provideDfModuleAdvancedCommerce(),
        provideDfModuleImage(),
        provideDfModuleElasticsearch(),
        provideDfModuleGiftcards(),
        provideDfModuleSentry({
            dsn: 'https://27b53afd20b17555c5aa5b90c6140bab@sentry.c1a.tomandco.uk/51'
        }),
        provideDfModuleHeaderUi({
            headerComponentId: headerComponentId,
            fields: {
                theme: {
                    options: {
                        transparentLight: 'Transparent background with white copy',
                        transparentDark: 'Transparent background with dark copy'
                    },
                    check: app => (app.meta.model?.type === 'page' ? undefined : '')
                }
            }
        }),
        provideDfModuleThumbor({
            defaultOptions: {
                quality: 70
            }
        }),
        provideDfModuleKlevuSearch(),
        provideDfModuleLoqate({
            emailKey: 'XU99-ZH95-CU58-KA77',
            addressKey: 'XU99-ZH95-CU58-KA77',
            enabled: false // typeof window !== 'undefined' && ['uk'].includes(window.dfConfig.ms.siteId as string)
        }),
        provideDfModuleOneTrust(),
        provideDfModuleHeaderUi({
            headerComponentId: headerComponentId,
            fields: {
                theme: {
                    options: {
                        transparentLight: 'Transparent background with white copy',
                        transparentDark: 'Transparent background with dark copy'
                    },
                    check: app => (app.meta.model?.type === 'page' ? undefined : '')
                }
            }
        }),
        provideDfModuleGoogleMaps({
            mapOptions: {
                zoom: 12,
                streetViewControl: true,
                scrollwheel: false
            },
            storeToMarker: (store: Store, options: google.maps.MarkerOptions) => {
                return {
                    ...options,
                    info: store.data
                        ? `
                                    <div class="center font-default p-a-1">
                                        <h5 class="h4 p-b-1">${store.data.store_name}</h5>
                                        <p class="p-b-1 font-2 fs-4">${store.data.address}</p>
                                        <p class="font-2"><a class="col-2" href="tel:${store.data.phone}">${store.data.phone}</a></p>
                                    </div>`
                        : ''
                };
            }
        }),
        provideDfModulePca(),
        provideDfSearch({
            listing: {
                size: [
                    {
                        size: 1,
                        onSize: ['S', 'M']
                    },
                    {
                        size: 2,
                        onSize: ['S', 'M']
                    },
                    {
                        size: 3,
                        onSize: ['L', 'X']
                    },
                    {
                        size: 4,
                        onSize: ['L', 'X']
                    }
                ]
            },
            options: {
                search: {
                    stockCheck: false
                },
                view: {
                    stockCheck: false
                }
            }
        }),
        provideDfSession(),
        provideDfSsr(),
        provideCmsComponents(),
        provideAppInitializer(() => {
            const categoryResource = inject(CategoryResource);
            const gtmService = inject(GtmService);
            const localRespectMustLoginService = inject(LocalRespectMustLoginService);
            const stripeService = inject(StripeService);
            const attributeResource = inject(AttributeResource);
            const customformResource = inject(CustomformResource);
            const multisite = inject(MultisiteService);
            const uiConfig = inject(NUi.OPTIONS);

            const euroWebsites = ['ie', 'eu', 'fr', 'de', 'nl', 'eu_trade', 'fr_trade', 'de_trade', 'nl_trade'];
            const isEuroWebsite = euroWebsites.includes(multisite.siteId as string);
            uiConfig.price.symbolAfterPrice = isEuroWebsite;
            uiConfig.price.decimalSeparator = isEuroWebsite ? ',' : '.';
            uiConfig.price.symbolSeparator = isEuroWebsite ? ' ' : '';
        }),
        importProvidersFrom(
            ICmsClientModule.forRoot(ICMS_CONFIG),
            CustomerGroupsModule.forRoot(),
            DfModuleGtmModule.forRoot(),
            DfModulePaypalModule.forRoot(),

            DfModuleStripeModule,
            EpsilonModule,
            HubspotModule,
            AccountInformationCentreModule,
            VipModule,
            BasketCarryOverModule,
            ModalModule
        ),
        { provide: Basket, useExisting: LocalBasket },
        { provide: CustomerNotLoggedGuard, useExisting: LocalCustomerNotLoggedGuard },
        { provide: CustomerLoggedGuard, useExisting: LocalCustomerLoggedGuard },
        { provide: MentionMeService, useClass: LocalMentionMeService },
        { provide: GtmService, useExisting: LocalGtmService },
        { provide: NeedLoginService, useExisting: NeedLoginServiceDecorator },
        { provide: ICmsPageResource, useClass: LocalPageResource },
        { provide: ProductListingFactory, useClass: ProductListingFactoryDecorator },
        { provide: AdvancedCommerceService, useExisting: LocalAdvancedCommerceService },
        { provide: KlevuTrackService, useExisting: LocalKlevuTrackService },
        { provide: KlevuSearchService, useExisting: LocalKlevuSearchService },
        { provide: HistoryOrder, useExisting: LocalHistoryOrder },
        {
            provide: SearchService,
            useFactory: () => {
                const advancedCommerceConfigStore = inject(AdvancedCommerceConfigStore);
                if (advancedCommerceConfigStore.config()?.enabled) {
                    return inject(AdvancedCommerceSearchService);
                } else {
                    return inject(LocalKlevuSearchService);
                }
            }
        },
        { provide: SearchResultFactoryService, useClass: LocalSearchResultFactoryService },
        { provide: SearchResult, useClass: LocalSearchResult },
        {
            provide: LOCALE_ID,
            useFactory: (app: App) => {
                const lang = app.dfConfig.ms?.sites[app.dfConfig.ms.siteId]?.lang?.[0] || 'en';
                return lang === 'x-default' ? 'en' : lang;
            },
            deps: [App]
        },
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'disabled' // we have our own implementation that does that
            })
        )
    ]
};
